import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { locations } from '@/pages/location/locationData';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://mowandsnow.ca';

const sitemapSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/sitemap`,
  name: 'HTML Sitemap — Mow and Snow',
  description: 'Full sitemap for Mow and Snow: all service pages and GTA location pages for lawn care, snow removal, and hardscaping.',
  url: `${siteUrl}/sitemap`,
  isPartOf: { '@id': `${siteUrl}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Sitemap', item: `${siteUrl}/sitemap` },
    ],
  },
};

const mainPages = [
  { label: 'Home', href: '/', desc: 'Mow and Snow homepage — lawn care & snow removal across the GTA', icon: 'ri-home-5-line' },
  { label: 'About Us', href: '/#about', desc: 'Our story, mission, and why GTA homeowners trust us', icon: 'ri-information-line' },
  { label: 'Our Services', href: '/#services', desc: 'Lawn mowing, gardening, yard cleanups, snow removal & hardscaping', icon: 'ri-scissors-cut-line' },
  { label: 'Pricing', href: '/#pricing', desc: 'Simple flat-rate monthly plans — Basic, Complete, and Premium', icon: 'ri-price-tag-3-line' },
  { label: 'Service Areas', href: '/#service-areas', desc: 'All GTA cities and neighbourhoods we currently serve', icon: 'ri-map-pin-2-line' },
  { label: 'Contact', href: '/#contact', desc: 'Get a free quote or reach us directly', icon: 'ri-customer-service-2-line' },
];

const serviceLinks = [
  { label: 'Lawn Mowing & Edging', href: '/#services', icon: 'ri-plant-line' },
  { label: 'Gardening & Planting', href: '/#services', icon: 'ri-leaf-line' },
  { label: 'Yard Cleanups', href: '/#services', icon: 'ri-recycle-line' },
  { label: 'Snow Removal & Plowing', href: '/#services', icon: 'ri-snowy-line' },
  { label: 'Hardscaping — Driveways & Patios', href: '/#services', icon: 'ri-road-map-line' },
];

export default function SitemapPage() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(sitemapSchema) }} />
      <title>HTML Sitemap | Mow and Snow — GTA Lawn Care & Snow Removal</title>
      <meta name="description" content="Explore the full sitemap for Mow and Snow. Find all service pages, location-specific lawn care and snow removal pages, and contact information." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${siteUrl}/sitemap`} />

      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Banner */}
        <section className="pt-32 pb-14 bg-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-white/50 text-sm mb-6 flex-wrap">
              <Link to="/" className="hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-white/30"></i>
              <span className="text-white/80 whitespace-nowrap">Sitemap</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
              Site<span className="text-yellow-400">map</span>
            </h1>
            <p className="text-white/70 text-lg max-w-2xl">
              Every page on the Mow and Snow website — from our services overview to city-specific lawn care and snow removal pages across the Greater Toronto Area.
            </p>
          </div>
        </section>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Main Pages */}
          <section className="mb-16" aria-labelledby="main-pages-heading">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-black rounded-lg">
                <i className="ri-layout-4-line text-yellow-400 text-xl"></i>
              </div>
              <h2 id="main-pages-heading" className="text-2xl font-bold text-gray-900">Main Pages</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mainPages.map((page) => (
                <a
                  key={page.label}
                  href={page.href}
                  className="group flex items-start gap-4 p-5 rounded-xl border border-gray-100 bg-gray-50 hover:border-yellow-300 hover:bg-yellow-50 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-gray-200 group-hover:border-yellow-300 transition-all shrink-0">
                    <i className={`${page.icon} text-gray-700 group-hover:text-yellow-500 text-lg transition-colors`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 group-hover:text-yellow-600 transition-colors whitespace-nowrap">{page.label}</p>
                    <p className="text-sm text-gray-500 mt-0.5 leading-snug">{page.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </section>

          {/* Services */}
          <section className="mb-16" aria-labelledby="services-heading">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-black rounded-lg">
                <i className="ri-tools-line text-yellow-400 text-xl"></i>
              </div>
              <h2 id="services-heading" className="text-2xl font-bold text-gray-900">Our Services</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {serviceLinks.map((svc) => (
                <a
                  key={svc.label}
                  href={svc.href}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-green-300 hover:bg-green-50 transition-all cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-gray-200 group-hover:border-green-300 transition-all shrink-0">
                    <i className={`${svc.icon} text-gray-600 group-hover:text-green-600 text-base transition-colors`}></i>
                  </div>
                  <span className="font-medium text-gray-800 group-hover:text-green-700 transition-colors">{svc.label}</span>
                </a>
              ))}
            </div>
          </section>

          {/* Location Pages */}
          <section aria-labelledby="locations-heading">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center bg-black rounded-lg">
                <i className="ri-map-pin-line text-yellow-400 text-xl"></i>
              </div>
              <h2 id="locations-heading" className="text-2xl font-bold text-gray-900">GTA Location Pages</h2>
            </div>
            <p className="text-gray-500 text-sm mb-6 ml-[52px]">
              City-specific lawn care, gardening, yard cleanup, snow removal, and hardscaping pages across Ontario.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {locations.map((loc) => (
                <Link
                  key={loc.slug}
                  to={`/locations/${loc.slug}`}
                  className="group p-5 rounded-xl border border-gray-100 bg-gray-50 hover:border-yellow-300 hover:bg-yellow-50 transition-all cursor-pointer block"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-map-pin-2-fill text-yellow-500 group-hover:text-yellow-600 text-lg transition-colors"></i>
                    </div>
                    <span className="font-semibold text-gray-900 group-hover:text-yellow-700 transition-colors whitespace-nowrap">{loc.name}</span>
                  </div>
                  <p className="text-sm text-gray-500 leading-snug pl-10 line-clamp-2">{loc.description}</p>
                  <div className="mt-3 pl-10 flex flex-wrap gap-1">
                    {['Lawn', 'Snow', 'Cleanups', 'Hardscaping'].map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-500 group-hover:border-yellow-200 group-hover:text-yellow-600 transition-colors whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>

            {/* Neighbourhood hint */}
            <div className="mt-8 p-5 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center shrink-0">
                <i className="ri-question-line text-gray-400 text-2xl"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-700">Don&apos;t see your city?</p>
                <p className="text-sm text-gray-500">We cover the entire Greater Toronto Area. <a href="/#contact" className="text-yellow-600 hover:underline font-medium cursor-pointer">Contact us</a> and we&apos;ll confirm your coverage zone.</p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
